import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import Snackbar from '@material-ui/core/Snackbar';
import { SnackbarContent } from '@material-ui/core';

import useRoomState from '../../hooks/useRoomState/useRoomState';
import { callSecondsLeft, formatCountdown, WARNING_SECONDS } from '../../utils/callTimeLimit';

const useStyles = makeStyles({
  snackbar: {
    backgroundColor: '#6db1ff',
    color: '#0b1b2b',
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: '0.8em',
  },
  timer: {
    marginLeft: '0.4em',
    fontVariantNumeric: 'tabular-nums',
  },
  // Visible to screen readers only.
  announcement: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
});

// What a screen reader is told. It changes only at these points, so the alert is read a few times
// and not once a second as the clock ticks.
function announcement(seconds: number): string {
  if (seconds <= 0) return 'This call is ending now.';
  if (seconds <= 60) return 'This call will end in 1 minute.';
  return 'This call will end in 5 minutes.';
}

/**
 * A countdown for the last five minutes before the participant time limit ends the call (the host's limit
 * ends it for everyone). Nothing is shown before that.
 */
export default function CallTimeWarning() {
  const classes = useStyles();
  const roomState = useRoomState();
  const [seconds, setSeconds] = useState<number | null>(() => callSecondsLeft());

  useEffect(() => {
    const update = () => setSeconds(callSecondsLeft());
    update();
    const timer = window.setInterval(update, 1000);
    // A background tab's timer is throttled, so recompute the moment it is visible again.
    document.addEventListener('visibilitychange', update);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  const inCall = roomState === 'connected' || roomState === 'reconnecting';
  const open = inCall && seconds !== null && seconds <= WARNING_SECONDS;

  return (
    <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={open}>
      <SnackbarContent
        className={classes.snackbar}
        // SnackbarContent is role="alert" by default, which would read out every tick of the clock.
        // The announcement below is the only alert.
        role="presentation"
        message={
          <span className={classes.message}>
            <InfoIcon className={classes.icon} aria-hidden="true" />
            <span aria-hidden="true">
              {seconds === 0 ? 'This call is ending now.' : 'This call will end in'}
              {seconds ? <strong className={classes.timer}>{formatCountdown(seconds)}</strong> : null}
            </span>
            {open && seconds !== null && (
              <span role="alert" className={classes.announcement}>
                {announcement(seconds)}
              </span>
            )}
          </span>
        }
      />
    </Snackbar>
  );
}
